import React from 'react';
import PropTypes from 'prop-types';
import {generateTestIds, deprecatedProp} from '@homebase/common';

// exported for tests only
export function getButtonAttributes({
  // Ignore the component prop, we don't want to pass it down to the element
  Component,
  is,
  testIds,
  ...props
}) {
  // If there is an href we will render an anchor tag, so we want to format the props for an <a>
  // Note: If its disabled, we still end up rendering an anchor tag
  // this is to preserve the behavior on site.
  if (props.href) {
    const {
      type,
      disabled,
      openInNewWindow,
      innerRef,
      noreferrer,
      ...rest
    } = props;

    const relAttributes = noreferrer ? 'noopener noreferrer' : 'noopener';

    return {
      ...rest,
      ...generateTestIds({testIds}),
      rel: openInNewWindow ? relAttributes : null,
      target: openInNewWindow ? '_blank' : null,
      ref: innerRef,
    };
  }

  // Otherwise we will render a <button> element
  const {
    openInNewWindow,
    innerRef,
    href,
    disabled,
    noreferrer,
    ...rest
  } = props;
  return {
    ...rest,
    ...generateTestIds({testIds}),
    ref: innerRef,
    // This `|| null` is to preserve the default behavior from before
    // It also prevents adding the disabled attribute to the element if its falsy
    disabled: disabled || null,
  };
}

const BaseButton = (props) => {
  const {
    Component,
    is,
    openInNewWindow,
    innerRef,
    testIds,
    noreferrer,
    ...rest
  } = props;
  if (Component) {
    if (typeof Component === 'string') {
      return <Component {...generateTestIds({testIds})} {...rest} />;
    } else {
      return (
        <Component
          testIds={testIds}
          openInNewWindow={openInNewWindow}
          innerRef={innerRef}
          noreferrer={noreferrer}
          {...rest}
        />
      );
    }
  }
  if (is) {
    const CustomComponent = is;
    if (typeof CustomComponent === 'string') {
      return <CustomComponent {...generateTestIds({testIds})} {...rest} />;
    } else {
      return (
        <CustomComponent
          testIds={testIds}
          openInNewWindow={openInNewWindow}
          innerRef={innerRef}
          noreferrer={noreferrer}
          {...rest}
        />
      );
    }
  }
  const Element = props.href ? 'a' : 'button';
  const attributes = getButtonAttributes(props);
  return <Element {...attributes} />;
};

BaseButton.propTypes = {
  href: PropTypes.string,
  Component: deprecatedProp('Please use the `is` prop instead.'),
  // The HTML element the component will render as.
  is: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.string,
  ]),
  openInNewWindow: PropTypes.bool,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  type: PropTypes.oneOf(['button', 'reset', 'submit', null]),
  disabled: PropTypes.bool,
  innerRef: PropTypes.func,
  // Optional Enzyme and Codeception testing identifiers
  testIds: PropTypes.shape({
    enzyme: PropTypes.string,
    codeception: PropTypes.string,
  }),
  // Optional prop used in combination with the openInNewWindow prop, to add noreferrer to the rel attribute
  // Should be set to false for internal links only due to no support for `noopener` on IE11/Edge
  // https://caniuse.com/#search=noopener
  // Without `noreferrer` there is a security risk for external links
  // https://mathiasbynens.github.io/rel-noopener/
  // https://github.com/yannickcr/eslint-plugin-react/blob/master/docs/rules/jsx-no-target-blank.md
  noreferrer: PropTypes.bool,
};

BaseButton.defaultProps = {
  href: null,
  is: null,
  openInNewWindow: false,
  onClick: null,
  onMouseEnter: null,
  onMouseLeave: null,
  onBlur: null,
  onFocus: null,
  type: null,
  disabled: false,
  innerRef: null,
  testIds: {},
  noreferrer: true,
};

export default BaseButton;
